import {
  Box,
  TextField,
  Autocomplete,
  Tooltip,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import { DeleteOutlined } from "@mui/icons-material";
import VirtualizedAutocomplete from "../../virtualizedAutocomplete";
import { Variaveis } from "../../../pages/easyReport/function/editFunction"; // adjust import if needed

interface Application {
  name: string;
}

type Applications = Application[];

type ItemOption = { name: string; key_: string; applicationName: string };

interface VariaveisFormProps {
  variaveis: Variaveis[];
  setVariaveis: (
    fn: (prev: { variavel: Variaveis[] }) => { variavel: Variaveis[] },
  ) => void;
  allApplications: Applications;
  itemsOptions: ItemOption[][];
  itemsLoading: boolean;
  onApplicationChange: (
    index: number,
    v: Variaveis,
    newValue: Application[] | null,
  ) => void;
  onDelete: (index: number) => void;
  isLarge: boolean;
  styles: any;
  theme: any;
  isFieldDuplicated: (
    vars: Variaveis[],
    value: string,
    field: "nome" | "title",
  ) => boolean;
  isDefault?: boolean;
}

export default function VariaveisForm({
  variaveis,
  setVariaveis,
  allApplications,
  itemsOptions,
  itemsLoading,
  onApplicationChange,
  onDelete,
  isLarge,
  styles,
  theme,
  isFieldDuplicated,
  isDefault,
}: VariaveisFormProps) {
  const isCustom = new Map();
  itemsOptions.forEach((item, index) => {
    isCustom.set(index, item[0]?.applicationName === "CUSTOM");
  });

  function handleKeyNameChange(
    index: number,
    setVariaveis: VariaveisFormProps["setVariaveis"],
  ) {
    return (_: any, newValues: any) => {
      let value = newValues;
      if (isCustom.get(index) === true) {
        value =
          Array.isArray(value) && value.length > 0
            ? [value[value.length - 1]]
            : [];
      }
      const keyNameArray = Array.isArray(newValues)
        ? value
            .filter(
              (
                nv: any,
              ): nv is {
                name: string;
                key_: string;
                applicationName: string;
              } =>
                typeof nv === "object" &&
                nv !== null &&
                "name" in nv &&
                "key_" in nv &&
                "applicationName" in nv,
            )
            .map((nv: any) => ({
              key: nv.key_,
              applicationName: nv.applicationName,
            }))
        : [];
      setVariaveis((prev) => ({
        ...prev,
        variavel: prev.variavel.map((items, i) =>
          i === index
            ? {
                ...items,
                keyName: keyNameArray,
              }
            : items,
        ),
      }));
    };
  }

  return (
    <>
      {variaveis.map((v, index) => (
        <div key={index}>
          <Box sx={styles.variableContainer}>
            <Box sx={{ flex: 1 }}>
              <Box sx={styles.boxTitle}>
                <Typography variant="subtitle2">Label</Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Label da variável"
                slotProps={{
                  input: {
                    sx: {
                      color: theme.palette.text.secondary,
                    },
                  },
                }}
                value={v.title}
                onChange={(e) =>
                  setVariaveis((prev) => ({
                    ...prev,
                    variavel: prev.variavel.map((item, i) =>
                      i === index
                        ? {
                            ...item,
                            title: e.target.value || "",
                          }
                        : item,
                    ),
                  }))
                }
                error={isFieldDuplicated(variaveis, v.title, "title")}
                helperText={
                  isFieldDuplicated(variaveis, v.title, "title")
                    ? "Label da variável duplicado"
                    : "O nome que será exibido"
                }
                disabled={isDefault}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={styles.boxTitle}>
                <Typography variant="subtitle2">Variável</Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Nome da variável"
                slotProps={{
                  input: {
                    sx: {
                      color: theme.palette.text.secondary,
                    },
                  },
                }}
                value={v.nome}
                onChange={(e) =>
                  setVariaveis((prev) => ({
                    ...prev,
                    variavel: prev.variavel.map((item, i) =>
                      i === index
                        ? {
                            ...item,
                            nome: e.target.value || "",
                          }
                        : item,
                    ),
                  }))
                }
                error={isFieldDuplicated(variaveis, v.nome, "nome")}
                helperText={
                  isFieldDuplicated(variaveis, v.nome, "nome")
                    ? "Nome da variável duplicado"
                    : "O nome que será usado no código"
                }
                disabled={isDefault}
              />
            </Box>
            <Box sx={{ flex: -2, mb: 3 }}>
              <Box sx={styles.boxTitle}>
                <Typography variant="subtitle2">Aplicação</Typography>
              </Box>
              <Autocomplete
                fullWidth
                multiple
                size="small"
                limitTags={1}
                disableCloseOnSelect
                options={allApplications}
                getOptionLabel={(option) => option.name || ""}
                value={allApplications.filter((app) =>
                  v?.applicationId?.includes(app.name),
                )}
                onChange={(_, newValue) =>
                  onApplicationChange(index, v, newValue)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Aplicação"
                    variant="outlined"
                  />
                )}
                renderOption={(props, option, { selected }) => {
                  const { key, ...rest } = props;
                  return (
                    <li key={key} {...rest}>
                      <Checkbox checked={selected} />
                      {option.name}
                    </li>
                  );
                }}
                disabled={isDefault}
              />
            </Box>
            <Box sx={{ flex: 1, mb: 3 }}>
              <Box sx={styles.boxTitle}>
                <Typography variant="subtitle2">Itens</Typography>
              </Box>
              <VirtualizedAutocomplete
                fullWidth
                multiple
                size="small"
                limitTags={1}
                disableCloseOnSelect
                sx={styles.autocomplete}
                loading={itemsLoading}
                options={itemsOptions[index] || []}
                value={(itemsOptions[index] || []).filter((item) =>
                  v.keyName.some(
                    (k) =>
                      k.key === item.key_ &&
                      k.applicationName === item.applicationName,
                  ),
                )}
                filterOptions={(options, { inputValue }) =>
                  options.filter(
                    (option) =>
                      option.name
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()) ||
                      option.key_
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()),
                  )
                }
                disabled={isDefault}
                onChange={handleKeyNameChange(index, setVariaveis)}
                renderOption={(props, option, { selected }) => {
                  const { key, ...rest } = props;
                  return (
                    <li key={key} {...rest}>
                      <Checkbox checked={selected} />
                      <Tooltip
                        title={
                          <Box>
                            <Typography sx={{ fontSize: 11 }}>
                              {option.name}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 11 }}
                              color="text.secondary"
                            >
                              {option.key_}
                            </Typography>
                          </Box>
                        }
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography
                            sx={{
                              maxWidth: 250,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {option.name}
                          </Typography>
                          <Typography
                            sx={{
                              maxWidth: 250,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              color: theme.palette.text.disabled,
                            }}
                          >
                            {option.key_}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Itens"
                    variant="outlined"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {itemsLoading ? (
                              <CircularProgress size={22} sx={{ mr: 1 }} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
              }}
            >
              {v.applicationId.length >= 0 &&
              !v?.applicationId?.includes("CUSTOM") ? (
                <>
                  <Box sx={styles.boxTitle}>
                    <Typography variant="subtitle2">Múltiplos</Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                      mb: 3,
                    }}
                  >
                    <FormControl
                      sx={{ ...styles.multiBox, flexShrink: 0 }}
                      size="small"
                    >
                      {/* <FormLabel id="row-radio-buttons-group-label">
                    Multiplos
                  </FormLabel> */}
                      <RadioGroup
                        row
                        aria-labelledby="row-radio-buttons-group-label"
                        name="radio-buttons-group"
                        value={v.multiple ? "true" : "false"}
                        onChange={(_, newValue) => {
                          setVariaveis((prev) => ({
                            ...prev,
                            variavel: prev.variavel.map((item, i) =>
                              i === index
                                ? { ...item, multiple: newValue === "true" }
                                : item,
                            ),
                          }));
                        }}
                      >
                        <FormControlLabel
                          value="false"
                          control={
                            <Radio
                              disabled={isDefault}
                              sx={{ transform: "scale(0.9)" }}
                              size="small"
                            />
                          }
                          label="Não"
                        />
                        <FormControlLabel
                          value="true"
                          control={
                            <Radio
                              disabled={isDefault}
                              sx={{ transform: "scale(0.9)" }}
                              size="small"
                            />
                          }
                          label="Sim"
                        />
                      </RadioGroup>
                    </FormControl>
                    {!isLarge ? (
                      <IconButton
                        onClick={() => onDelete(index)}
                        disabled={variaveis.length <= 1 || isDefault}
                        sx={{ ...styles.iconButton, flex: 1, minWidth: 0 }}
                      >
                        <DeleteOutlined sx={{ fontSize: 30 }} />
                      </IconButton>
                    ) : null}
                  </Box>
                </>
              ) : !isLarge ? (
                <IconButton
                  onClick={() => onDelete(index)}
                  disabled={variaveis.length <= 1 || isDefault}
                  sx={{ ...styles.iconButton, flex: 1, minWidth: 0 }}
                >
                  <DeleteOutlined sx={{ fontSize: 30 }} />
                </IconButton>
              ) : null}
            </Box>
          </Box>
          {isLarge ? (
            <Button
              variant="outlined"
              color="error"
              disabled={variaveis.length <= 1 || isDefault}
              sx={{ flex: 1, minWidth: 0 }}
              onClick={() => onDelete(index)}
            >
              <DeleteOutlined sx={{ fontSize: 30 }} /> Deletar Variável
            </Button>
          ) : null}
          {isLarge && <Divider sx={{ my: 1 }} />}
        </div>
      ))}
    </>
  );
}
